// 事案一覧に関するロジックをまとめたHooks
import { useCallback } from 'react';
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentUser } from 'aws-amplify/auth';

import { datacrudApiClient as client } from '../utils/apiClient';
import type { components } from '../schema/data-crud';
import { useSelectedOrCurrentYear, useSelectedYear } from './useYears';

// scopeをfireDepartmentとするアカウントのリスト
const whiteListUsers = [
  'fujisawakyuukyuu', // 救命課
  'shirei29', // 指令室
];
const getScope = async () => {
  const user = await getCurrentUser();
  const scope = whiteListUsers.includes(user.username) ? 'fireDepartment' : 'team';
  return scope;
}; // TODO: scopeではなくサーバーサイドの権限で閲覧できる事案をコントロールしたい

const getJianList = async (year: string, limit: number, nextToken?: string | undefined) => {
  const scope = await getScope();
  const res = await client.GET('/jians', {
    params: {
      query: {
        year,
        scope,
        limit,
        nextToken,
      },
    },
  });
  if (res.error) {
    throw new Error(`Failed to GET /jians \n message: ${res.error.message}`);
  }
  return res.data;
};

const currentPageState = atom<number>({
  key: 'useJianList/currentPageState',
  default: 0,
});

const limitState = atom<number>({
  key: 'useJianList/limitState',
  default: 10,
});

export const useJianList = () => {
  const [page, setPage] = useRecoilState(currentPageState);
  const [limit, setLimit] = useRecoilState(limitState);
  const year = useSelectedOrCurrentYear();
  const queryClient = useQueryClient();

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery<
    components['schemas']['JianListResponse']
  >({
    queryKey: ['jians', { year, limit }],
    queryFn: async ({ pageParam = undefined }) => {
      return await getJianList(year, limit, pageParam);
    },
    getNextPageParam: (lastPage) => lastPage.nextToken,
  });

  const isLatestPage = data?.pages.length === page + 1;
  const isLastPage = isLatestPage && !hasNextPage;

  const incrementPage = useCallback(() => {
    if (isLastPage) return;
    if (isLatestPage) fetchNextPage();
    setPage(page + 1);
  }, [setPage, fetchNextPage, page, isLastPage, isLatestPage]);

  const decrementPage = useCallback(() => {
    setPage(page > 0 ? page - 1 : 0);
  }, [setPage, page]);

  const changeLimit = useCallback(
    (newLimit: number) => {
      setPage(0);
      setLimit(newLimit);
      queryClient.removeQueries(['jians', { year }]);
    },
    [fetchNextPage, setPage, setLimit],
  );

  return {
    jianList: data?.pages.at(page),
    page,
    limit,
    changeLimit,
    isLastPage,
    incrementPage,
    decrementPage,
  };
};

export const useRefreshJianList = () => {
  const queryClient = useQueryClient();
  const setPage = useSetRecoilState(currentPageState);
  const year = useSelectedYear();

  return useCallback(() => {
    queryClient.resetQueries(['jians', { year }]);
    setPage(0);
  }, [setPage, queryClient, year]);
};
